import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import BrandMark from '../BrandMark';
import config from '../../config';
import { publicApi } from './api';
import './BuzzerSolo.css';

function Stars({ value, onChange }) {
  return (
    <div className="bsolo-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" className={`bsolo-star ${value >= n ? 'active' : ''}`} onClick={() => onChange(n)}>
          {n}
        </button>
      ))}
    </div>
  );
}

export default function BuzzerSoloPublicPage() {
  const { token } = useParams();
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({ orgServiceRate: 5, qualityCostRate: 5, environmentCleanRate: 5, comment: '', email: '' });

  const tableLabel = useMemo(() => (status?.tableNumbers || []).join(', '), [status]);
  const logoUrl = useMemo(() => `${config.API_BASE_URL}/public/buzzersolo/${token}/logo`, [token]);

  const load = useCallback(async () => {
    try {
      const { data } = await publicApi.get(`/public/buzzersolo/${token}/status`);
      setStatus(data);
    } catch (error) {
      setMessage(error?.response?.data?.error || 'This QR is not available anymore.');
    }
  }, [token]);

  useEffect(() => {
    load();
    const id = window.setInterval(load, 5000);
    return () => window.clearInterval(id);
  }, [load]);

  const callService = async () => {
    try {
      await publicApi.post(`/buzzersolo/public/${token}/call`);
      setMessage('Service has been notified.');
      await load();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Failed to call service');
    }
  };

  const submitFeedback = async () => {
    try {
      await publicApi.post(`/public/buzzersolo/${token}/feedback`, form);
      setMessage('Thank you for your feedback.');
      await load();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Failed to submit feedback');
    }
  };

  return (
    <div className="bsolo-page">
      <div className="bsolo-public">
        <div className="bsolo-public-header">
          {status?.hasClientLogo ? (
            <div className="bsolo-public-logo-wrap">
              <img src={logoUrl} alt={status?.venueName || 'Venue logo'} />
            </div>
          ) : null}
          <div>
            <div className="bsolo-pill bsolo-pill--accent">Powered by EstiaSync Solo</div>
            <h1 className="bsolo-title" style={{ marginTop: 12, textAlign: 'center' }}>{status?.venueName || 'Your venue'}</h1>
            <div className="bsolo-meta">Table {tableLabel || '...'}</div>
          </div>
          {status?.clientBrandUrl ? (
            <a className="bsolo-link-chip" href={status.clientBrandUrl} target="_blank" rel="noreferrer">Visit venue website</a>
          ) : null}
        </div>

        <div className="bsolo-public-card">
          {message ? <div className="bsolo-card" style={{ marginBottom: 14 }}>{message}</div> : null}

          {status?.stage === 'active_ready_to_call' && (
            <div>
              <p className="bsolo-muted">Press the button below to notify the service team.</p>
              <div className="bsolo-actions"><button className="bsolo-btn" onClick={callService}>Call service</button></div>
            </div>
          )}

          {status?.stage === 'call_sent' && (
            <div>
              <p className="bsolo-muted">The service team has been notified. Please wait a moment.</p>
              <div className="bsolo-actions"><button className="bsolo-btn-secondary" onClick={load}>Refresh status</button></div>
            </div>
          )}

          {status?.stage === 'released_feedback_pending' && (
            <div className="bsolo-stack">
              <p className="bsolo-muted">Before you leave, please rate your experience.</p>

              <div className="bsolo-field">
                <label>Service & management</label>
                <Stars value={form.orgServiceRate} onChange={(v) => setForm((s) => ({ ...s, orgServiceRate: v }))} />
              </div>
              <div className="bsolo-field">
                <label>Quality & value</label>
                <Stars value={form.qualityCostRate} onChange={(v) => setForm((s) => ({ ...s, qualityCostRate: v }))} />
              </div>
              <div className="bsolo-field">
                <label>Environment & cleanliness</label>
                <Stars value={form.environmentCleanRate} onChange={(v) => setForm((s) => ({ ...s, environmentCleanRate: v }))} />
              </div>
              <div className="bsolo-field">
                <label>Comment</label>
                <textarea rows="4" value={form.comment} onChange={(e) => setForm((s) => ({ ...s, comment: e.target.value }))} />
              </div>
              <div className="bsolo-field">
                <label>Email (optional)</label>
                <input value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} />
              </div>
              <div className="bsolo-meta">By sending your email you accept the terms of use and privacy note of the venue.</div>
              <div className="bsolo-actions"><button className="bsolo-btn" onClick={submitFeedback}>Send feedback</button></div>
            </div>
          )}

          {status?.stage === 'feedback_submitted' && (
            <div>
              <p className="bsolo-muted">Thank you. Your feedback was submitted successfully.</p>
            </div>
          )}

          {status?.stage === 'expired' && (
            <div>
              <p className="bsolo-muted">This QR has expired.</p>
            </div>
          )}
        </div>

        <div style={{ display: 'grid', placeItems: 'center', marginTop: 18 }}>
          <BrandMark href="https://estiasync.com" size={26} />
        </div>
      </div>
    </div>
  );
}
